import React from 'react';
import DeliveryMap from './DeliveryMap';
import { partnerAPI } from '../services/api';

interface Order {
  _id: string;
  user?: { name: string; phone: string; location?: { lat: number; lng: number } };
  items: Array<{ name: string; quantity: string; icon: string }>;
  deliveryAddress: {
    addressLine: string;
    city?: string;
    pincode?: string;
    location?: { lat: number; lng: number };
  };
  status: string;
  totalAmount: number;
  itemsTotal?: number;
  deliveryCharges?: number;
  deliveryConfirmation?: {
    otp?: string;
    partnerConfirmed?: boolean;
    customerConfirmed?: boolean;
  };
  isDirectCustomOrder?: boolean;
  customOrderRequest?: {
    description?: string;
    requestedItems?: Array<{ name: string; quantity: string }>;
  };
}

interface CurrentOrderProps {
  order: Order | null;
  onUpdateStatus: (status: string, data?: any) => void;
  onBack: () => void;
  onChat?: (orderId: string, customerName: string, customerPhone: string) => void;
}

const CurrentOrder: React.FC<CurrentOrderProps> = ({ order, onUpdateStatus, onBack, onChat }) => {
  const [showBillForm, setShowBillForm] = React.useState(false);
  const [itemsTotal, setItemsTotal] = React.useState('');
  const [billImage, setBillImage] = React.useState('');
  const [deliveryCharges, setDeliveryCharges] = React.useState('50');
  const [deliveryOTP, setDeliveryOTP] = React.useState('');
  const [otpGenerated, setOtpGenerated] = React.useState(false);
  const [demoOTP, setDemoOTP] = React.useState('');
  
  // Direct Custom Order - Payment Request States
  const [showPaymentRequest, setShowPaymentRequest] = React.useState(false);
  const [qrCodeImage, setQrCodeImage] = React.useState('');
  const [storePhone, setStorePhone] = React.useState('');
  const [storeName, setStoreName] = React.useState('');
  const [paymentAmount, setPaymentAmount] = React.useState('');
  const [sendingPaymentRequest, setSendingPaymentRequest] = React.useState(false);
  const [paymentRequestSent, setPaymentRequestSent] = React.useState(false);
  const [customerReceiptImage, setCustomerReceiptImage] = React.useState('');
  
  if (!order) {
    return (
      <div style={styles.container}>
        <button onClick={onBack} style={styles.backBtn}>← Back</button>
        <div style={styles.emptyState}>
          <h2>📦 No Active Order</h2>
          <p>Accept an order to start delivering</p>
        </div>
      </div>
    );
  }

  const getNextStatus = () => {
    // Direct custom order flow
    if (order.isDirectCustomOrder) {
      switch (order.status) {
        case 'Partner Assigned':
          return 'Payment Received'; // Wait for customer payment
        case 'Payment Received':
          return 'Purchasing Items';
        case 'Purchasing Items':
          return 'Out for Delivery';
        case 'Out for Delivery':
          return 'At Customer Location';
        case 'At Customer Location':
          return 'Delivered';
        default:
          return null;
      }
    }
    
    // Regular order flow
    switch (order.status) {
      case 'Confirmed':
        return 'Partner En Route to Store';
      case 'Partner En Route to Store':
        return 'At Store';
      case 'At Store':
        return 'Items Packed';
      case 'Items Packed':
      case 'Payment Pending':
        return null; // Wait for customer payment
      case 'Payment Confirmed':
        return 'Out for Delivery';
      case 'Out for Delivery':
        return 'At Customer Location';
      case 'At Customer Location':
        return 'Delivered';
      default:
        return null;
    }
  };

  const getStatusButton = () => {
    // Direct custom order flow - step by step updates to customer
    if (order.isDirectCustomOrder) {
      switch (order.status) {
        case 'Partner Assigned':
          return { text: '💰 Request Payment from Customer', status: 'Waiting for Payment', showPaymentForm: true };
        case 'Waiting for Payment':
          return null; // Wait for customer payment confirmation
        case 'Payment Received':
          return { text: '🛒 Starting to Purchase Items - Thank You!', status: 'Purchasing Items' };
        case 'Purchasing Items':
          return { text: '🚴 Items Ready - Starting Delivery', status: 'Out for Delivery' };
        case 'Out for Delivery':
          return { text: '📍 I\'m at Your Location', status: 'At Customer Location' };
        case 'At Customer Location':
          return { text: '✅ Order Delivered - Confirm & Close', status: 'Delivered' };
        default:
          return null;
      }
    }
    
    // Regular order flow
    switch (order.status) {
      case 'Confirmed':
        return { text: '🚴 En Route to Store', status: 'Partner En Route to Store' };
      case 'Partner En Route to Store':
        return { text: '🏪 Reached Store', status: 'At Store' };
      case 'At Store':
        return { text: '📦 Items Packed - Enter Bill', status: 'Items Packed', needsBill: true };
      case 'Payment Pending':
        return null; // Wait for customer
      case 'Payment Confirmed':
        return { text: '🚴 Start Delivery', status: 'Out for Delivery' };
      case 'Out for Delivery':
        return { text: '📍 Reached Customer', status: 'At Customer Location' };
      case 'At Customer Location':
        return { text: '✅ Confirm Delivery', status: 'Delivered' };
      default:
        return null;
    }
  };

  const handleStatusUpdate = (status: string) => {
    if (status === 'Items Packed') {
      setShowBillForm(true);
    } else {
      onUpdateStatus(status);
    }
  };

  const handleBillSubmit = () => {
    if (!itemsTotal || parseFloat(itemsTotal) <= 0) {
      alert('Please enter a valid bill amount');
      return;
    }

    const billData = {
      status: 'Items Packed',
      itemsTotal: parseFloat(itemsTotal),
      deliveryCharges: parseFloat(deliveryCharges),
      billImage: billImage || undefined
    };

    onUpdateStatus('Items Packed', billData);
    setShowBillForm(false);
    setItemsTotal('');
    setBillImage('');
  };

  const handleGenerateDeliveryOTP = async () => {
    try {
      const response = await partnerAPI.generateDeliveryOTP(order!._id);
      setOtpGenerated(true);
      if (response.data.demoOTP) {
        setDemoOTP(response.data.demoOTP);
        alert(`DEMO MODE: OTP sent to customer!\nCustomer's OTP: ${response.data.demoOTP}`);
      } else {
        alert('OTP sent to customer! Ask customer for the OTP.');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to generate OTP');
    }
  };

  const handleConfirmDelivery = async () => {
    if (!deliveryOTP || deliveryOTP.length !== 6) {
      alert('Please enter the 6-digit OTP from customer');
      return;
    }

    try {
      await partnerAPI.confirmDelivery(order!._id, { otp: deliveryOTP, tip: 0 });
      alert('Delivery confirmed! ✅');
      setDeliveryOTP('');
      setOtpGenerated(false);
      setDemoOTP('');
      onUpdateStatus('Delivered');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Invalid OTP. Please try again.');
    }
  };

  const nextStatus = getNextStatus();
  const statusButton = getStatusButton();

  return (
    <div style={styles.container}>
      <button onClick={onBack} style={styles.backBtn}>← Back</button>

      <div style={styles.card}>
        <div style={{
          ...styles.statusBanner,
          ...(order.isDirectCustomOrder ? { background: 'linear-gradient(135deg, #7c3aed, #ec4899)' } : {})
        }}>
          <h2 style={{ margin: 0 }}>
            {order.isDirectCustomOrder && '🛒 '}
            Current Status: {order.status}
          </h2>
          {order.isDirectCustomOrder && (
            <span style={{ fontSize: '12px', opacity: 0.9 }}>Direct Custom Order</span>
          )}
        </div>

        {/* Direct Custom Order Info */}
        {order.isDirectCustomOrder && order.customOrderRequest && (
          <div style={{
            background: '#f3e8ff',
            padding: '15px',
            borderRadius: '12px',
            margin: '15px 0',
            border: '2px solid #c4b5fd'
          }}>
            <h3 style={{ margin: '0 0 10px', color: '#7c3aed', fontSize: '16px' }}>📝 Customer's Request</h3>
            {order.customOrderRequest.description && (
              <p style={{ margin: '0 0 10px', color: '#5b21b6', fontSize: '14px' }}>
                {order.customOrderRequest.description}
              </p>
            )}
            {order.customOrderRequest.requestedItems && order.customOrderRequest.requestedItems.length > 0 && (
              <div>
                <strong style={{ fontSize: '13px', color: '#6b21a8' }}>Items to Purchase:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {order.customOrderRequest.requestedItems.map((item, idx) => (
                    <li key={idx} style={{ fontSize: '14px', color: '#7c3aed', marginBottom: '5px' }}>
                      <strong>{item.name}</strong> - {item.quantity}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{
              background: '#fef3c7',
              padding: '10px 12px',
              borderRadius: '8px',
              marginTop: '10px'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
                💰 <strong>Delivery Charge: ₹{order.deliveryCharges || 60}</strong> (Prepaid by customer)
              </p>
            </div>
          </div>
        )}

        {order.user && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>👤 Customer</h3>
            <p><strong>{order.user.name}</strong></p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <a href={`tel:${order.user.phone}`} style={styles.phoneLink}>
                📞 {order.user.phone}
              </a>
              {onChat && (
                <button
                  onClick={() => onChat(order._id, order.user!.name, order.user!.phone)}
                  style={styles.chatButton}
                >
                  💬 Chat
                </button>
              )}
            </div>
          </div>
        )}

        {order.deliveryAddress && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📍 Delivery Address</h3>
            <p style={styles.address}>
              {order.deliveryAddress.addressLine}
              {order.deliveryAddress.city && `, ${order.deliveryAddress.city}`}
              {order.deliveryAddress.pincode && ` - ${order.deliveryAddress.pincode}`}
            </p>
            
            {/* Navigation Map - Show when order is confirmed or in delivery */}
            {['Confirmed', 'Partner En Route to Store', 'At Store', 'Items Packed', 'Payment Confirmed', 'Out for Delivery', 'At Customer Location'].includes(order.status) && (
              <DeliveryMap
                customerAddress={order.deliveryAddress.addressLine}
                customerLocation={order.deliveryAddress.location || order.user?.location}
                orderStatus={order.status}
                orderId={order._id}
              />
            )}
          </div>
        )}

        {order.items && order.items.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🛒 Items</h3>
            <div style={styles.itemsList}>
              {order.items.map((item, idx) => (
                <div key={idx} style={styles.item}>
                  <span style={styles.itemIcon}>{item.icon}</span>
                  <div>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{item.name}</p>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>{item.quantity}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={styles.totalSection}>
          <h2 style={{ margin: 0 }}>Total: ₹{order.totalAmount}</h2>
          {(order as any).tip > 0 && (
            <p style={{ margin: '5px 0 0', color: '#28a745', fontWeight: 'bold' }}>
              💰 Tip: ₹{(order as any).tip}
            </p>
          )}
          <p style={{ margin: '5px 0 0', color: '#60b246' }}>You'll earn: ₹{order.deliveryCharges || 50}</p>
        </div>

        {order.status === 'Payment Pending' && (!order.itemsTotal || order.itemsTotal === 0) && (
          <div style={{...styles.waitingBanner, background: '#dc143c'}}>
            ⚠️ Bill amount not set! Please enter bill details below.
          </div>
        )}

        {order.status === 'Payment Pending' && order.itemsTotal && order.itemsTotal > 0 && (
          <div style={styles.waitingBanner}>
            ⏳ Waiting for customer payment confirmation...
            <p style={{margin: '10px 0 0', fontSize: '14px'}}>
              Bill Amount: ₹{order.itemsTotal} + Delivery: ₹{order.deliveryCharges || 50} = Total: ₹{order.totalAmount}
            </p>
          </div>
        )}

        {order.status === 'Payment Pending' && (!order.itemsTotal || order.itemsTotal === 0) && (
          <button
            onClick={() => setShowBillForm(true)}
            style={{...styles.updateBtn, background: '#fc8019', marginTop: '15px'}}
          >
            📝 Enter Bill Details Now
          </button>
        )}

        {showBillForm && (
          <div style={styles.billForm}>
            <h3 style={{margin: '0 0 15px', color: '#282c3f'}}>📋 Enter Bill Details</h3>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Items Total (₹) *</label>
              <input
                type="number"
                value={itemsTotal}
                onChange={(e) => setItemsTotal(e.target.value)}
                placeholder="Enter total bill amount"
                style={styles.input}
                step="0.01"
                min="0"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Delivery Charges (₹)</label>
              <input
                type="number"
                value={deliveryCharges}
                onChange={(e) => setDeliveryCharges(e.target.value)}
                style={styles.input}
                step="0.01"
                min="0"
              />
            </div>

            {itemsTotal && deliveryCharges && (
              <div style={styles.totalPreview}>
                <strong>Total Amount:</strong> ₹{(parseFloat(itemsTotal) + parseFloat(deliveryCharges)).toFixed(2)}
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Bill Image URL (Optional)</label>
              <input
                type="text"
                value={billImage}
                onChange={(e) => setBillImage(e.target.value)}
                placeholder="https://example.com/bill.jpg"
                style={styles.input}
              />
            </div>

            <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
              <button onClick={handleBillSubmit} style={styles.submitBtn}>
                ✅ Submit Bill
              </button>
              <button onClick={() => setShowBillForm(false)} style={styles.cancelBtn}>
                ❌ Cancel
              </button>
            </div>
          </div>
        )}

        {statusButton && !showBillForm && statusButton.status !== 'Delivered' && !showPaymentRequest && 
         !(order.isDirectCustomOrder && ['Payment Received', 'Purchasing Items', 'Out for Delivery', 'At Customer Location'].includes(order.status)) && (
          <button
            onClick={() => {
              // For direct custom orders, show payment request form
              if (order.isDirectCustomOrder && (statusButton as any).showPaymentForm) {
                setShowPaymentRequest(true);
              } else {
                handleStatusUpdate(statusButton.status);
              }
            }}
            style={styles.updateBtn}
          >
            {statusButton.text}
          </button>
        )}

        {/* Direct Custom Order - Payment Request Form (Partner at store, sends QR to customer) */}
        {order.isDirectCustomOrder && showPaymentRequest && (
          <div style={{
            background: '#f3e8ff',
            padding: '20px',
            borderRadius: '15px',
            marginTop: '20px',
            border: '2px solid #c4b5fd'
          }}>
            <h3 style={{ margin: '0 0 15px', color: '#7c3aed', fontSize: '18px', textAlign: 'center' }}>
              📸 Send Payment Details to Customer
            </h3>
            
            <p style={{ fontSize: '13px', color: '#6b21a8', textAlign: 'center', marginBottom: '20px' }}>
              Take a photo of the store's QR code and enter the amount
            </p>

            {/* Camera for QR Code */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: '600', color: '#5b21b6', display: 'block', marginBottom: '8px' }}>
                📷 Store QR Code Photo
              </label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setQrCodeImage(reader.result as string);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px dashed #c4b5fd',
                  borderRadius: '10px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              />
              {qrCodeImage && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                  <img 
                    src={qrCodeImage} 
                    alt="QR Code" 
                    style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '10px' }} 
                  />
                </div>
              )}
            </div>

            {/* Store Name */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: '600', color: '#5b21b6', display: 'block', marginBottom: '8px' }}>
                🏪 Store Name
              </label>
              <input
                type="text"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                placeholder="Enter store name"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #c4b5fd',
                  borderRadius: '10px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Store Phone */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: '600', color: '#5b21b6', display: 'block', marginBottom: '8px' }}>
                📞 Store Phone Number
              </label>
              <input
                type="tel"
                value={storePhone}
                onChange={(e) => setStorePhone(e.target.value)}
                placeholder="Store phone number"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #c4b5fd',
                  borderRadius: '10px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Amount */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontWeight: '600', color: '#5b21b6', display: 'block', marginBottom: '8px' }}>
                💰 Total Amount to Pay
              </label>
              <input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount in ₹"
                style={{
                  width: '100%',
                  padding: '15px',
                  border: '2px solid #c4b5fd',
                  borderRadius: '10px',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowPaymentRequest(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!qrCodeImage) {
                    alert('Please take a photo of the store QR code');
                    return;
                  }
                  if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
                    alert('Please enter a valid amount');
                    return;
                  }

                  setSendingPaymentRequest(true);
                  try {
                    await partnerAPI.sendPaymentRequest(order._id, {
                      qrCodeImage,
                      storePhone,
                      storeName,
                      amount: parseFloat(paymentAmount),
                      message: `Please pay ₹${paymentAmount} to ${storeName || 'store'} for your items`
                    });
                    alert('✅ Payment details sent to customer!');
                    setPaymentRequestSent(true);
                    setShowPaymentRequest(false);
                    onUpdateStatus('Waiting for Payment');
                  } catch (error: any) {
                    alert(error.response?.data?.message || 'Failed to send payment request');
                  } finally {
                    setSendingPaymentRequest(false);
                  }
                }}
                disabled={sendingPaymentRequest}
                style={{
                  flex: 2,
                  padding: '12px',
                  background: sendingPaymentRequest ? '#9ca3af' : 'linear-gradient(135deg, #7c3aed, #ec4899)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: sendingPaymentRequest ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '15px'
                }}
              >
                {sendingPaymentRequest ? '⏳ Sending...' : '📤 Send to Customer'}
              </button>
            </div>
          </div>
        )}

        {/* Direct Custom Order - Waiting for Customer Payment */}
        {order.isDirectCustomOrder && order.status === 'Waiting for Payment' && (
          <div style={{
            background: '#fef3c7',
            padding: '20px',
            borderRadius: '15px',
            marginTop: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>⏳</div>
            <h3 style={{ margin: '0 0 10px', color: '#92400e' }}>Waiting for Customer Payment</h3>
            <p style={{ margin: 0, color: '#b45309', fontSize: '14px' }}>
              Customer is completing payment to store. Please wait...
            </p>
            
            <button
              onClick={() => {
                // Confirm payment received
                onUpdateStatus('Payment Received');
                alert('✅ Payment confirmed! Now purchase the items.');
              }}
              style={{
                marginTop: '15px',
                padding: '12px 25px',
                background: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '15px'
              }}
            >
              ✅ Customer Payment Received - Start Shopping
            </button>
          </div>
        )}

        {/* Direct Custom Order - Step by Step Progress after Payment */}
        {order.isDirectCustomOrder && ['Payment Received', 'Purchasing Items', 'Out for Delivery', 'At Customer Location'].includes(order.status) && (
          <div style={{
            background: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
            padding: '20px',
            borderRadius: '15px',
            marginTop: '20px',
            border: '2px solid #86efac'
          }}>
            <h3 style={{ margin: '0 0 15px', color: '#166534', textAlign: 'center' }}>
              📦 Order Progress
            </h3>
            
            {/* Progress Steps */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', position: 'relative' }}>
              {/* Progress line */}
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '10%',
                right: '10%',
                height: '3px',
                background: '#e5e7eb',
                zIndex: 0
              }} />
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '10%',
                width: order.status === 'Payment Received' ? '0%' : 
                       order.status === 'Purchasing Items' ? '33%' : 
                       order.status === 'Out for Delivery' ? '66%' : '80%',
                height: '3px',
                background: '#22c55e',
                zIndex: 1,
                transition: 'width 0.3s ease'
              }} />
              
              {/* Step 1 */}
              <div style={{ textAlign: 'center', zIndex: 2 }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: order.status !== 'Payment Received' ? '#22c55e' : '#fbbf24',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {order.status !== 'Payment Received' ? '✓' : '1'}
                </div>
                <div style={{ fontSize: '11px', color: '#166534' }}>Payment</div>
              </div>
              
              {/* Step 2 */}
              <div style={{ textAlign: 'center', zIndex: 2 }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: ['Out for Delivery', 'At Customer Location'].includes(order.status) ? '#22c55e' : 
                              order.status === 'Purchasing Items' ? '#fbbf24' : '#e5e7eb',
                  color: ['Out for Delivery', 'At Customer Location', 'Purchasing Items'].includes(order.status) ? 'white' : '#9ca3af',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {['Out for Delivery', 'At Customer Location'].includes(order.status) ? '✓' : '2'}
                </div>
                <div style={{ fontSize: '11px', color: '#166534' }}>Shopping</div>
              </div>
              
              {/* Step 3 */}
              <div style={{ textAlign: 'center', zIndex: 2 }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: order.status === 'At Customer Location' ? '#22c55e' : 
                              order.status === 'Out for Delivery' ? '#fbbf24' : '#e5e7eb',
                  color: ['Out for Delivery', 'At Customer Location'].includes(order.status) ? 'white' : '#9ca3af',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {order.status === 'At Customer Location' ? '✓' : '3'}
                </div>
                <div style={{ fontSize: '11px', color: '#166534' }}>Delivery</div>
              </div>
              
              {/* Step 4 */}
              <div style={{ textAlign: 'center', zIndex: 2 }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#e5e7eb',
                  color: '#9ca3af',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  4
                </div>
                <div style={{ fontSize: '11px', color: '#166534' }}>Complete</div>
              </div>
            </div>
            
            {/* Current Status Message */}
            <div style={{
              background: 'white',
              padding: '15px',
              borderRadius: '10px',
              textAlign: 'center',
              marginBottom: '15px'
            }}>
              {order.status === 'Payment Received' && (
                <>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>💳✅</div>
                  <div style={{ fontWeight: 'bold', color: '#166534', marginBottom: '5px' }}>Payment Received!</div>
                  <div style={{ fontSize: '13px', color: '#666' }}>Thank you for the payment. Start purchasing items now.</div>
                </>
              )}
              {order.status === 'Purchasing Items' && (
                <>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>🛒</div>
                  <div style={{ fontWeight: 'bold', color: '#166534', marginBottom: '5px' }}>Purchasing Items...</div>
                  <div style={{ fontSize: '13px', color: '#666' }}>Buying items for the customer. Click when ready to deliver.</div>
                </>
              )}
              {order.status === 'Out for Delivery' && (
                <>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>🚴</div>
                  <div style={{ fontWeight: 'bold', color: '#166534', marginBottom: '5px' }}>On the Way!</div>
                  <div style={{ fontSize: '13px', color: '#666' }}>Heading to customer location. Click when you arrive.</div>
                </>
              )}
              {order.status === 'At Customer Location' && (
                <>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>📍</div>
                  <div style={{ fontWeight: 'bold', color: '#166534', marginBottom: '5px' }}>At Customer Location</div>
                  <div style={{ fontSize: '13px', color: '#666' }}>Hand over the items and confirm delivery.</div>
                </>
              )}
            </div>
            
            {/* Action Button - Send update to customer */}
            {statusButton && (
              <button
                onClick={() => {
                  onUpdateStatus(statusButton.status);
                  if (statusButton.status !== 'Delivered') {
                    alert(`✅ Status updated! Customer notified: "${statusButton.text}"`);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '15px',
                  background: order.status === 'At Customer Location' 
                    ? 'linear-gradient(135deg, #22c55e, #16a34a)' 
                    : 'linear-gradient(135deg, #7c3aed, #ec4899)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                }}
              >
                {statusButton.text}
              </button>
            )}
            
            {/* Earning Info */}
            <div style={{
              marginTop: '15px',
              padding: '10px',
              background: '#fef3c7',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '13px', color: '#92400e' }}>
                💰 You'll earn <strong>₹{order.deliveryCharges || 60}</strong> after delivery
              </span>
            </div>
          </div>
        )}

        {/* New Delivery OTP Flow: Customer receives OTP, Partner enters it */}
        {order.status === 'At Customer Location' && !order.isDirectCustomOrder && !otpGenerated && (
          <button
            onClick={handleGenerateDeliveryOTP}
            style={{...styles.updateBtn, background: '#fc8019'}}
          >
            📱 Generate Delivery OTP (Send to Customer)
          </button>
        )}

        {order.status === 'At Customer Location' && otpGenerated && (
          <div style={styles.otpSection}>
            <div style={styles.otpBanner}>
              🔐 Customer has received OTP via SMS
              {demoOTP && (
                <p style={{fontSize: '14px', margin: '5px 0 0', color: '#28a745'}}>
                  <strong>DEMO MODE:</strong> Customer's OTP is {demoOTP}
                </p>
              )}
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Enter OTP from Customer</label>
              <input
                type="text"
                value={deliveryOTP}
                onChange={(e) => setDeliveryOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit OTP"
                style={{...styles.input, fontSize: '20px', textAlign: 'center', letterSpacing: '5px'}}
                maxLength={6}
              />
            </div>

            <button
              onClick={handleConfirmDelivery}
              style={{...styles.updateBtn, background: '#28a745'}}
              disabled={deliveryOTP.length !== 6}
            >
              ✅ Confirm Delivery with OTP
            </button>
          </div>
        )}

        {order.status === 'Delivered' && (
          <div style={styles.completedBanner}>
            ✅ Order Completed! ₹50 credited to your account.
          </div>
        )}
      </div>
    </div>
  );
};

const styles: any = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px',
  },
  backBtn: {
    padding: '10px 20px',
    background: '#666',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '15px',
  },
  card: {
    background: 'white',
    borderRadius: '15px',
    padding: '25px',
    maxWidth: '600px',
    margin: '0 auto',
  },
  statusBanner: {
    background: '#fc8019',
    color: 'white',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '25px',
    textAlign: 'center',
  },
  section: {
    marginBottom: '25px',
    paddingBottom: '20px',
    borderBottom: '1px solid #e0e0e0',
  },
  sectionTitle: {
    fontSize: '18px',
    color: '#333',
    marginBottom: '10px',
  },
  phoneLink: {
    display: 'inline-block',
    padding: '10px 20px',
    background: '#60b246',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: 'bold',
  },
  chatButton: {
    background: '#007bff',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  address: {
    color: '#333',
    lineHeight: 1.6,
    fontSize: '16px',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '12px',
    background: '#f9f9f9',
    borderRadius: '10px',
  },
  itemIcon: {
    fontSize: '32px',
  },
  totalSection: {
    background: '#f0f0f0',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  updateBtn: {
    width: '100%',
    padding: '18px',
    background: '#60b246',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  completedBanner: {
    background: '#60b246',
    color: 'white',
    padding: '15px',
    borderRadius: '10px',
    textAlign: 'center',
    marginTop: '15px',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  waitingBanner: {
    background: '#ffa500',
    color: 'white',
    padding: '15px',
    borderRadius: '10px',
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    marginTop: '15px',
  },
  otpBanner: {
    background: '#5d8ed5',
    color: 'white',
    padding: '20px',
    borderRadius: '10px',
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    marginTop: '15px',
  },
  otpSection: {
    marginTop: '20px',
  },
  billForm: {
    background: '#f9f9f9',
    padding: '20px',
    borderRadius: '12px',
    border: '2px solid #fc8019',
    marginTop: '15px',
  },
  formGroup: {
    marginBottom: '15px',
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '600',
    color: '#282c3f',
    fontSize: '14px',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '2px solid #d4d5d9',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
  },
  totalPreview: {
    background: '#fff5ed',
    padding: '15px',
    borderRadius: '8px',
    textAlign: 'center',
    fontSize: '18px',
    color: '#fc8019',
    marginBottom: '15px',
    border: '2px solid #fc8019',
  },
  submitBtn: {
    flex: 1,
    padding: '14px',
    background: '#60b246',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  cancelBtn: {
    flex: 1,
    padding: '14px',
    background: '#dc143c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};

export default CurrentOrder;
